import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type {
  CompensationRule,
  ReportFact,
  ReportFilterState,
  SavedReportTemplate,
} from '@/lib/reporting/types';
import { RETENTION_FOLLOW_UP_DAYS } from '@/lib/reporting/types';
import { resolveReportPeriod, toIsoRange } from '@/lib/reporting/periods';

function rpc<T>(name: string, args: Record<string, unknown>) {
  return supabase.rpc(name as never, args as never) as unknown as Promise<{
    data: T | null;
    error: Error | null;
  }>;
}

function from(table: string) {
  return supabase.from(table as never);
}

export function reportFactsQueryKey(
  clinicId: string | null,
  range: { start: string; end: string },
  filters: ReportFilterState,
) {
  return ['practitioner-report-facts', clinicId, range, filters] as const;
}

function filtersToRpc(filters: ReportFilterState) {
  const appointmentStatuses =
    filters.appointmentStatus === 'all' ? [] : [filters.appointmentStatus];
  return {
    practitioner_ids: filters.practitionerIds,
    service_ids: filters.serviceIds,
    location_ids: filters.locationIds,
    appointment_statuses: appointmentStatuses,
    patient_ids: filters.patientIds,
    invoice_statuses: filters.invoiceStatuses,
    payment_methods: filters.paymentMethods,
    include_cancelled:
      filters.includeCancelled ||
      filters.appointmentStatus === 'all' ||
      filters.appointmentStatus === 'cancelled',
    include_no_shows:
      filters.includeNoShows ||
      filters.appointmentStatus === 'all' ||
      filters.appointmentStatus === 'no_show',
  };
}

function asFact(row: Record<string, unknown>): ReportFact {
  return {
    fact_kind: row.fact_kind === 'product_sale' ? 'product_sale' : 'appointment',
    appointment_id: (row.appointment_id as string | null) ?? null,
    invoice_id: (row.invoice_id as string | null) ?? null,
    invoice_line_id: (row.invoice_line_id as string | null) ?? null,
    punch_card_id: (row.punch_card_id as string | null) ?? null,
    clinic_id: String(row.clinic_id),
    clinic_name: (row.clinic_name as string | null) ?? null,
    practitioner_id: (row.practitioner_id as string | null) ?? null,
    practitioner_name: (row.practitioner_name as string | null) ?? null,
    service_id: (row.service_id as string | null) ?? null,
    service_name: (row.service_name as string | null) ?? null,
    patient_id: (row.patient_id as string | null) ?? null,
    patient_name: (row.patient_name as string | null) ?? null,
    starts_at: String(row.starts_at),
    ends_at: String(row.ends_at),
    duration_minutes: Number(row.duration_minutes ?? 0),
    appointment_status: (row.appointment_status as string | null) ?? null,
    visit_status: (row.visit_status as string | null) ?? null,
    billing_method: (row.billing_method as string | null) ?? null,
    billing_status: (row.billing_status as string | null) ?? null,
    payment_method: (row.payment_method as string | null) ?? null,
    invoice_status: (row.invoice_status as string | null) ?? null,
    currency: String(row.currency ?? 'DKK'),
    invoiced_amount_cents: Number(row.invoiced_amount_cents ?? 0),
    paid_amount_cents: Number(row.paid_amount_cents ?? 0),
    outstanding_amount_cents: Number(row.outstanding_amount_cents ?? 0),
    discount_cents: Number(row.discount_cents ?? 0),
    refund_cents: Number(row.refund_cents ?? 0),
    product_sales_cents: Number(row.product_sales_cents ?? 0),
    commission_percent: row.commission_percent == null ? null : Number(row.commission_percent),
    compensation_model: (row.compensation_model as string | null) ?? null,
    is_new_patient: Boolean(row.is_new_patient),
    returned_to_clinic: Boolean(row.returned_to_clinic),
    returned_to_practitioner: Boolean(row.returned_to_practitioner),
    patient_first_completed_at: (row.patient_first_completed_at as string | null) ?? null,
    last_in_period_at: (row.last_in_period_at as string | null) ?? null,
    issued_at: (row.issued_at as string | null) ?? null,
    paid_at: (row.paid_at as string | null) ?? null,
    source_type: (row.source_type as ReportFact['source_type']) ?? null,
    clips_redeemed: Number(row.clips_redeemed ?? 0),
    package_clip_count:
      row.package_clip_count == null ? null : Number(row.package_clip_count),
    package_sale_amount_cents:
      row.package_sale_amount_cents == null ? null : Number(row.package_sale_amount_cents),
    revenue_per_clip_cents:
      row.revenue_per_clip_cents == null ? null : Number(row.revenue_per_clip_cents),
  };
}

export function usePractitionerReportFacts(
  clinicId: string | null | undefined,
  filters: ReportFilterState,
  enabled: boolean,
) {
  const range = toIsoRange(
    resolveReportPeriod(filters.period, filters.customFrom, filters.customTo),
  );
  return useQuery({
    queryKey: reportFactsQueryKey(clinicId ?? null, range, filters),
    enabled: Boolean(clinicId) && enabled,
    queryFn: async (): Promise<ReportFact[]> => {
      if (!clinicId) return [];
      const { data, error } = await rpc<Record<string, unknown>[]>(
        'list_practitioner_report_facts',
        {
          p_clinic_id: clinicId,
          p_starts_at: range.start,
          p_ends_at: range.end,
          p_filters: filtersToRpc(filters),
          p_retention_follow_up_days: RETENTION_FOLLOW_UP_DAYS,
        },
      );
      if (error) throw error;
      return (data ?? []).map(asFact);
    },
    staleTime: 15_000,
  });
}

export const compensationRulesQueryKey = (clinicId: string | null, practitionerId: string | null) =>
  ['compensation-rules', clinicId, practitionerId] as const;

export function useCompensationRules(
  clinicId: string | null | undefined,
  practitionerId: string | null | undefined,
) {
  return useQuery({
    queryKey: compensationRulesQueryKey(clinicId ?? null, practitionerId ?? null),
    enabled: Boolean(clinicId) && Boolean(practitionerId),
    queryFn: async (): Promise<CompensationRule[]> => {
      const { data, error } = await from('practitioner_compensation_rules')
        .select('*')
        .eq('clinic_id', clinicId!)
        .eq('practitioner_id', practitionerId!)
        .order('effective_from', { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as CompensationRule[];
    },
  });
}

export function useSaveCompensationRule(clinicId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      practitionerId: string;
      serviceId: string | null;
      commissionPercent: number;
      effectiveFrom: string;
      closeExisting: boolean;
    }) => {
      if (!clinicId) throw new Error('No active clinic');
      if (input.closeExisting) {
        const fromDate = new Date(`${input.effectiveFrom}T00:00:00`);
        const previousDay = new Date(fromDate);
        previousDay.setDate(previousDay.getDate() - 1);
        const previousIso = previousDay.toISOString().slice(0, 10);
        let openQuery = from('practitioner_compensation_rules')
          .select('id, effective_from')
          .eq('clinic_id', clinicId)
          .eq('practitioner_id', input.practitionerId)
          .is('effective_to', null);
        if (input.serviceId) openQuery = openQuery.eq('service_id', input.serviceId);
        else openQuery = openQuery.is('service_id', null);
        const { data: openRules, error: openError } = await openQuery;
        if (openError) throw openError;
        for (const rule of (openRules ?? []) as Array<{ id: string; effective_from: string }>) {
          const closeOn = previousIso < rule.effective_from ? rule.effective_from : previousIso;
          const { error: closeError } = await from('practitioner_compensation_rules')
            .update({ effective_to: closeOn } as never)
            .eq('id', rule.id);
          if (closeError) throw closeError;
        }
      }
      const { data: authData } = await supabase.auth.getUser();
      const { data, error } = await from('practitioner_compensation_rules')
        .insert({
          clinic_id: clinicId,
          practitioner_id: input.practitionerId,
          service_id: input.serviceId,
          compensation_model: 'percentage_of_revenue',
          commission_percent: input.commissionPercent,
          effective_from: input.effectiveFrom,
          created_by: authData.user?.id ?? null,
        } as never)
        .select('*')
        .single();
      if (error) throw error;
      return data as unknown as CompensationRule;
    },
    onSuccess: (_row, vars) => {
      void qc.invalidateQueries({
        queryKey: compensationRulesQueryKey(clinicId ?? null, vars.practitionerId),
      });
    },
  });
}

export function useDeleteCompensationRule(clinicId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; practitionerId: string }) => {
      const { error } = await from('practitioner_compensation_rules').delete().eq('id', input.id);
      if (error) throw error;
    },
    onSuccess: (_void, vars) => {
      void qc.invalidateQueries({
        queryKey: compensationRulesQueryKey(clinicId ?? null, vars.practitionerId),
      });
    },
  });
}

export const reportTemplatesQueryKey = (clinicId: string | null) =>
  ['report-templates', clinicId] as const;

export function useReportTemplates(clinicId: string | null | undefined) {
  return useQuery({
    queryKey: reportTemplatesQueryKey(clinicId ?? null),
    enabled: Boolean(clinicId),
    queryFn: async (): Promise<SavedReportTemplate[]> => {
      const { data, error } = await from('report_templates')
        .select('*')
        .eq('clinic_id', clinicId!)
        .eq('report_type', 'practitioner')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as SavedReportTemplate[];
    },
  });
}

export function useSaveReportTemplate(clinicId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { name: string; config: SavedReportTemplate['config'] }) => {
      if (!clinicId) throw new Error('No active clinic');
      const { data: authData } = await supabase.auth.getUser();
      const { data, error } = await from('report_templates')
        .insert({
          clinic_id: clinicId,
          name: input.name.trim(),
          report_type: 'practitioner',
          config: input.config,
          created_by: authData.user?.id ?? null,
        } as never)
        .select('*')
        .single();
      if (error) throw error;
      return data as unknown as SavedReportTemplate;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: reportTemplatesQueryKey(clinicId ?? null) });
    },
  });
}

export function useDeleteReportTemplate(clinicId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await from('report_templates').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: reportTemplatesQueryKey(clinicId ?? null) });
    },
  });
}
